import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend( utc );

const IGNORED_DIFF_KEYS = new Set( [ '__typename' ] );

const pickContactFieldsForDiff = ( contact: any ) => {
	if ( !contact || typeof contact !== 'object' ) {
		return contact;
	}

	return {
		firstName: contact.firstName,
		lastName: contact.lastName,
		companyName: contact.companyName,
		customer:
			contact.customer && typeof contact.customer === 'object'
				? {
					email: contact.customer.email,
				  }
				: contact.customer,
	};
};

const pickEmailNotificationGroupFieldsForDiff = (
	emailNotificationGroup: any
) => {
	if ( !emailNotificationGroup || typeof emailNotificationGroup !== 'object' ) {
		return emailNotificationGroup;
	}

	if (
		Array.isArray( emailNotificationGroup.members ) &&
		emailNotificationGroup.members.length === 0
	) {
		return null;
	}

	return {
		members: emailNotificationGroup.members,
	};
};

export type Differences = {
	[key: string]: {
		oldValue: any;
		newValue: any;
		nested?: Differences;
	};
};

// Helper function to extract only the changed properties from nested objects
const extractChangedProperties = ( obj: any, differences: Differences ): any => {
	if ( !differences || Object.keys( differences ).length === 0 ) {
		return undefined;
	}

	const result: any = {};
	for ( const key in differences ) {
		if ( Object.prototype.hasOwnProperty.call( differences, key ) ) {
			const nested = differences[ key ].nested;
			if ( nested && Object.keys( nested ).length > 0 ) {
				// Recursively extract nested changed properties
				result[ key ] = extractChangedProperties( obj[ key ], nested );
			} else {
				// Include the actual value for leaf changes
				result[ key ] = obj[ key ];
			}
		}
	}
	return result;
};

// A helper to compare two objects and return the differences, if there are any
export const deepCompareAndDiff = <T extends object>(
	previous: T,
	updated: T,
	/* this last argument is so we can transform values prior to comparison
	For example dates with local timezones can be transformed to UTC for comparison */
	transformBeforeCompare?: Record<string, ( value: any ) => any>
): Differences | undefined => {
	const differences: Differences = {};

	// Check for differences in keys
	const allKeys = new Set(
		[ ...Object.keys( previous ), ...Object.keys( updated ) ].filter(
			( key ) => !IGNORED_DIFF_KEYS.has( key )
		)
	);

	for ( const key of allKeys ) {
		// the raw values
		const originalRaw1 = ( previous as any )[ key ];
		const originalRaw2 = ( updated as any )[ key ];
		const raw1 =
			key === 'contact'
				? pickContactFieldsForDiff( originalRaw1 )
				: key === 'emailNotificationGroup'
					? pickEmailNotificationGroupFieldsForDiff( originalRaw1 )
					: originalRaw1;
		const raw2 =
			key === 'contact'
				? pickContactFieldsForDiff( originalRaw2 )
				: key === 'emailNotificationGroup'
					? pickEmailNotificationGroupFieldsForDiff( originalRaw2 )
					: originalRaw2;

		// possibly transform the values prior to comparison
		const val1 = transformBeforeCompare?.[ key ]
			? transformBeforeCompare[ key ]( raw1 )
			: raw1;
		const val2 = transformBeforeCompare?.[ key ]
			? transformBeforeCompare[ key ]( raw2 )
			: raw2;

		// If one object has the key and the other doesn't
		if (
			Object.prototype.hasOwnProperty.call( previous, key ) &&
			!Object.prototype.hasOwnProperty.call( updated, key )
		) {
			differences[ key ] = { oldValue: val1, newValue: undefined };
			continue;
		}
		if (
			!Object.prototype.hasOwnProperty.call( previous, key ) &&
			Object.prototype.hasOwnProperty.call( updated, key )
		) {
			differences[ key ] = { oldValue: undefined, newValue: val2 };
			continue;
		}

		// If both are objects and not null, recurse
		if (
			typeof val1 === 'object' &&
			val1 !== null &&
			typeof val2 === 'object' &&
			val2 !== null
		) {
			const nestedDifferences = deepCompareAndDiff(
				val1,
				val2,
				transformBeforeCompare
			);
			if ( nestedDifferences && Object.keys( nestedDifferences ).length > 0 ) {
				// Only include the changed properties from nested objects
				const oldValueChanged = extractChangedProperties(
					val1,
					nestedDifferences
				);
				const newValueChanged = extractChangedProperties(
					val2,
					nestedDifferences
				);
				differences[ key ] = {
					oldValue: oldValueChanged,
					newValue: newValueChanged,
					nested: nestedDifferences,
				};
			}
		}
		// If values are not strictly equal
		else if ( val1 !== val2 ) {
			differences[ key ] = { oldValue: val1, newValue: val2 };
		}
	}

	if ( Object.keys( differences ).length === 0 ) {
		return undefined;
	}
	return differences;
};

export const transformDatesToLocalTimezone = ( value: any ) => {
	if ( value ) {
		return dayjs( value ).local().format( 'YYYY-MM-DD' );
	}
	return value;
};
